import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainLayout from "./layout/MainLayout";
import { AppProvider } from "../context/AppContext";
import { resolveRoleName } from "../utils/auth";
import {
  DEFAULT_SECTION,
  sectionToPath,
  getSectionFromPath,
} from "../routes/routeConfig";
import ProcessCreationPage from "../pages/ProcessCreationPage";
import WhatIfAnalysisPage from "../pages/WhatIfAnalysisPage";
import ProcessOptimizationPage from "../pages/ProcessOptimizationPage";
import ProcessOptimizationListPage from "../pages/ProcessOptimizationListPage";
import SubstitutionAnalystPage from "../pages/SubstitutionAnalystPage";
import CapiPaeiAnalysisPage from "../pages/CapiPaeiAnalysisPage";
import ProcessVisualizationPage from "../pages/ProcessVisualizationPage";
import DashboardPage from "../pages/DashboardPage";
import ProcessManagementPage from "../pages/ProcessManagementPage";
import ProcessViewPage from "../pages/ProcessViewPage";
import ProcessCreatePage from "../pages/ProcessCreatePage";
import ProcessEditPage from "../pages/ProcessEditPage";
import ProcessBuilderPage from "../pages/ProcessBuilderPage";
import ProcessCategoryAttributesPage from "../pages/ProcessCategoryAttributesPage";
import ProcessStatusManagementPage from "../pages/ProcessStatusManagementPage";
import TaskListTablePage from "../pages/TaskListTablePage";
import TaskCreatePage from "../pages/TaskCreatePage";
import TaskDetailPage from "../pages/TaskDetailPage";
import TaskEditPage from "../pages/TaskEditPage";
import TaskCategoryAttributesPage from "../pages/TaskCategoryAttributesPage";
import TaskStatusManagementPage from "../pages/TaskStatusManagementPage";
import JobManagementPage from "../pages/JobManagementPage";
import JobWorkloadPage from "../pages/JobWorkloadPage";
import JobDetailPage from "../pages/JobDetailPage";
import JobCreatePage from "../pages/JobCreatePage";
import JobEditPage from "../pages/JobEditPage";
import JobCategoryAttributesPage from "../pages/JobCategoryAttributesPage";
import JobStatusManagementPage from "../pages/JobStatusManagementPage";
import FunctionManagementPage from "../pages/FunctionManagementPage";
import FunctionDetailPage from "../pages/FunctionDetailPage";
import FunctionEditPage from "../pages/FunctionEditPage";
import FunctionCreatePage from "../pages/FunctionCreatePage";
import RoomManagementPage from "../pages/RoomManagementPage";
import AIProcessesPage from "../pages/AIProcessesPage";
import UserAccessControlPage from "../pages/UserAccessControlPage";
import AuditLogsPage from "../pages/AuditLogsPage";
import CompanyManagementPage from "../pages/CompanyManagementPage";
import RoleManagementPage from "../pages/RoleManagementPage";
import BuildingManagementPage from "../pages/BuildingManagementPage";
import BuildingDetailPage from "../pages/BuildingDetailPage";
import BuildingEditPage from "../pages/BuildingEditPage";
import BuildingCreatePage from "../pages/BuildingCreatePage";
import FloorManagementPage from "../pages/FloorManagementPage";
import FloorLayoutDesignerPage from "../pages/FloorLayoutDesignerPage";
import FloorCreatePage from "../pages/FloorCreatePage";
import FloorEditPage from "../pages/FloorEditPage";
import PeopleManagementPage from "../pages/PeopleManagementPage";
import PeopleCreatePage from "../pages/PeopleCreatePage";
import PeopleEditPage from "../pages/PeopleEditPage";
import PeopleViewPage from "../pages/PeopleViewPage";
import FunctionalHierarchyPage from "../pages/FunctionalHierarchyPage";
// C-Level (New) temporarily hidden (nav + URL)
// import CLevelLibraryNewPage from "../pages/c-level-library-new/CLevelLibraryNewPage";
import CLevelOrgChartPage from "../pages/CLevelOrgChartPage";
import Users3DManagementPage from "../pages/Users3DManagementPage";
import UnassignedEntitiesPage from "../pages/UnassignedEntitiesPage";
import BpmnViewerPage from "../pages/BpmnViewerPage";
import ProcessUseCasePage from "../pages/ProcessUseCasePage";
import HelpDocsPage from "../pages/HelpDocsPage";
import AssetTaskManagementPage from "../pages/AssetTaskManagementPage";
import AssetTaskCreatePage from "../pages/AssetTaskCreatePage";
import AssetTaskEditPage from "../pages/AssetTaskEditPage";
import AssetTaskDetailPage from "../pages/AssetTaskDetailPage";
import KPIManagementPage from "../pages/KPIManagementPage";
import KPICreatePage from "../pages/KPICreatePage";
import KPIEditPage from "../pages/KPIEditPage";
import UoMManagementPage from "../pages/UoMManagementPage";
import KPIAttributeManagementPage from "../pages/KPIAttributeManagementPage";
import ProcessKPIPage from "../pages/ProcessKPIPage";
import CountryManagementPage from "../pages/CountryManagementPage";
import CountryCreatePage from "../pages/CountryCreatePage";
import CountryEditPage from "../pages/CountryEditPage";
import CityManagementPage from "../pages/CityManagementPage";
import CityCreatePage from "../pages/CityCreatePage";
import CityEditPage from "../pages/CityEditPage";

// Sections only SUPER_ADMIN may open, mapped to the message shown to anyone else.
// Keep in sync with the Administration group in Sidebar.js.
const KPI_DENY = "Access denied: Only SUPER_ADMIN can access KPI Management.";
const ATTRIBUTES_DENY = "Access denied: Only SUPER_ADMIN can manage attributes.";
const SETTINGS_DENY = "Access denied: Only SUPER_ADMIN can manage settings.";
const ASSET_TASK_DENY =
  "Access denied: Only SUPER_ADMIN can manage Asset Tasks.";
const SUPER_ADMIN_ONLY_SECTIONS = {
  "role-management":
    "Access denied: Only SUPER_ADMIN can view Role Management.",
  "audit-logs": "Access denied: Only SUPER_ADMIN can view Audit Logs.",
  "kpi-management": KPI_DENY,
  "kpi-create": KPI_DENY,
  "kpi-edit": KPI_DENY,
  "uom-management": KPI_DENY,
  "kpi-attribute-management": KPI_DENY,
  "process-kpi": KPI_DENY,
  "process-category-attributes": ATTRIBUTES_DENY,
  "process-status": ATTRIBUTES_DENY,
  "task-category-attributes": ATTRIBUTES_DENY,
  "task-status": ATTRIBUTES_DENY,
  "job-category-attributes": ATTRIBUTES_DENY,
  "job-status": ATTRIBUTES_DENY,
  countries: SETTINGS_DENY,
  "country-create": SETTINGS_DENY,
  "country-edit": SETTINGS_DENY,
  cities: SETTINGS_DENY,
  "city-create": SETTINGS_DENY,
  "city-edit": SETTINGS_DENY,
  "asset-task-management": ASSET_TASK_DENY,
  "asset-task-create": ASSET_TASK_DENY,
  "asset-task-edit": ASSET_TASK_DENY,
  "asset-task-detail": "Access denied: Only SUPER_ADMIN can view Asset Tasks.",
};

const fillPathTemplate = (template, params) => {
  if (!template) return null;
  if (!template.includes(":")) return template;
  if (!params) return null;
  let missing = false;
  const filled = template.replace(/:([^/]+)/g, (_, key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") {
      missing = true;
      return _;
    }
    return encodeURIComponent(String(value));
  });
  return missing ? null : filled;
};

const Dashboard = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastSectionUrlRef = useRef({});
  const activeSection = useMemo(
    () => getSectionFromPath(location.pathname || "/"),
    [location.pathname],
  );
  useEffect(() => {
    if (!location.pathname || location.pathname === "/") {
      navigate(sectionToPath[DEFAULT_SECTION], { replace: true });
    }
  }, [location.pathname, navigate]);
  const [layoutCompanyId, setLayoutCompanyId] = useState(null);
  const [buildingId, setBuildingId] = useState(null);
  const [buildingFormMode, setBuildingFormMode] = useState("view");
  const [floorId, setFloorId] = useState(null);
  const [floorFormMode, setFloorFormMode] = useState("view");
  const [functionId, setFunctionId] = useState(null);
  const [functionFormMode, setFunctionFormMode] = useState("view");
  const [jobId, setJobId] = useState(null);
  const [jobFormMode, setJobFormMode] = useState("view");
  const [assetTaskId, setAssetTaskId] = useState(null);
  const [kpiId, setKpiId] = useState(null);
  const [roleName, setRoleName] = useState(null);
  const [roleResolved, setRoleResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!user) {
          setRoleName(null);
          return;
        }
        const name = await resolveRoleName(user);
        if (!cancelled) setRoleName(name || null);
      } catch {
        if (!cancelled) setRoleName(null);
      } finally {
        if (!cancelled) setRoleResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const section = activeSection;
    const sectionPath = sectionToPath[section];
    if (!sectionPath || sectionPath.includes(":")) return;
    lastSectionUrlRef.current[section] =
      `${location.pathname}${location.search || ""}`;
  }, [activeSection, location.pathname, location.search]);

  // Persist active section across refreshes in this session
  const handleSectionChange = useCallback(
    (section, params) => {
      const template = sectionToPath[section] || sectionToPath[DEFAULT_SECTION];
      if (!template) return;
      let targetPath = fillPathTemplate(template, params);
      if (!targetPath) return;

      if (!params && !template.includes(":")) {
        const remembered = lastSectionUrlRef.current[section];
        if (remembered) targetPath = remembered;
      }

      const currentPathWithSearch = `${location.pathname}${location.search || ""}`;
      if (currentPathWithSearch !== targetPath) {
        navigate(targetPath);
      }
    },
    [location.pathname, location.search, navigate],
  );

  const normalizedRole = String(roleName || "")
    .trim()
    .toUpperCase();
  const isSuperAdmin =
    normalizedRole === "SUPER_ADMIN" || normalizedRole === "SUPERADMIN";

  const renderAccessDenied = (message) => (
    <div style={{ padding: "1rem", color: "#b91c1c" }}>{message}</div>
  );

  const renderContent = () => {
    const superAdminDenyMessage = SUPER_ADMIN_ONLY_SECTIONS[activeSection];
    if (superAdminDenyMessage) {
      // Don't flash "Access denied" while the role is still being resolved.
      if (!roleResolved) {
        return <div style={{ padding: "1rem" }}>Loading...</div>;
      }
      if (!isSuperAdmin) return renderAccessDenied(superAdminDenyMessage);
    }

    switch (activeSection) {
      case "dashboard":
        return <DashboardPage />;
      case "process-management":
        return <ProcessManagementPage />;
      case "process-view":
        return <ProcessViewPage />;
      case "process-create":
        return <ProcessCreatePage />;
      case "process-edit":
        return <ProcessEditPage />;
      case "process-builder":
        return <ProcessBuilderPage />;
      case "process-category-attributes":
        return <ProcessCategoryAttributesPage />;
      case "process-status":
        return <ProcessStatusManagementPage />;
      case "process-creation":
        return <ProcessCreationPage />;
      case "task-management":
        return <TaskListTablePage />;
      case "task-create":
        return <TaskCreatePage />;
      case "task-detail":
        return <TaskDetailPage />;
      case "task-edit":
        return <TaskEditPage />;
      case "task-category-attributes":
        return <TaskCategoryAttributesPage />;
      case "task-status":
        return <TaskStatusManagementPage />;
      case "job-management":
        return <JobManagementPage />;
      case "job-workload":
        return <JobWorkloadPage />;
      case "job-detail":
        return <JobDetailPage />;
      case "job-create":
        return <JobCreatePage />;
      case "job-edit":
        return <JobEditPage />;
      case "job-category-attributes":
        return <JobCategoryAttributesPage />;
      case "job-status":
        return <JobStatusManagementPage />;
      case "function-management":
        return <FunctionManagementPage />;
      case "function-detail":
        return <FunctionDetailPage />;
      case "function-edit":
        return <FunctionEditPage />;
      case "function-create":
        return <FunctionCreatePage />;
      case "companies":
        return <CompanyManagementPage />;
      case "buildings":
        return <BuildingManagementPage />;
      case "building-create":
        return <BuildingCreatePage />;
      case "building-detail":
        return <BuildingDetailPage />;
      case "building-edit":
        return <BuildingEditPage />;
      case "building-floors":
        return <FloorManagementPage />;
      case "building-layout":
        return (
          <FloorLayoutDesignerPage
            buildingId={buildingId}
            onBack={() => handleSectionChange("building-floors")}
          />
        );
      case "floor-create":
        return <FloorCreatePage />;
      case "floor-edit":
        return <FloorEditPage mode="edit" />;
      // /floors/:id — the same page, read-only: it shows the design layout
      // rather than offering to change it.
      case "floor-detail":
        return <FloorEditPage mode="view" />;
      case "rooms":
        return <RoomManagementPage />;
      case "people-management":
        return <PeopleManagementPage />;
      case "people-create":
        return <PeopleCreatePage />;
      case "people-edit":
        return <PeopleEditPage />;
      case "people-view":
        return <PeopleViewPage />;
      case "what-if-analysis":
        return <WhatIfAnalysisPage />;
      case "process-optimization":
      case "process-optimization-copy":
        return <ProcessOptimizationListPage />;
      case "process-optimization-results":
        return <ProcessOptimizationPage />;
      case "substitution-analyst":
        return <SubstitutionAnalystPage />;
      case "capi-paei-analysis":
        return <CapiPaeiAnalysisPage />;
      case "process-visualization":
        return <ProcessVisualizationPage />;
      case "bpmn-viewer":
        return <BpmnViewerPage />;
      case "process-usecase":
        return <ProcessUseCasePage />;
      case "functional-hierarchy":
        return <FunctionalHierarchyPage />;
      // C-Level (New) temporarily hidden
      // case "c-level-library-new":
      //   return <CLevelLibraryNewPage />;
      case "c-level-org-chart":
        return <CLevelOrgChartPage />;
      case "unassigned-entities":
        return <UnassignedEntitiesPage />;
      case "help-docs":
      case "help-topic":
        return <HelpDocsPage />;
      // KPI Management - Only SUPER_ADMIN (guarded above)
      case "kpi-management":
        return <KPIManagementPage />;
      case "kpi-create":
        return <KPICreatePage />;
      case "kpi-edit":
        return <KPIEditPage />;
      case "uom-management":
        return <UoMManagementPage />;
      case "kpi-attribute-management":
        return <KPIAttributeManagementPage />;
      case "process-kpi":
        return <ProcessKPIPage />;
      // Backward compatibility for persisted sessions/older ids
      case "company-management":
      case "organization-management":
        return <CompanyManagementPage />;
      case "ai-processes":
        return <AIProcessesPage />;
      case "role-management":
        return <RoleManagementPage />;
      case "users-3d-management":
        if (normalizedRole === "NORMAL_USER") {
          return renderAccessDenied(
            "Access denied: NORMAL_USER cannot view 3D Users.",
          );
        }
        return <Users3DManagementPage />;
      case "user-access":
        if (normalizedRole === "NORMAL_USER") {
          return renderAccessDenied(
            "Access denied: NORMAL_USER cannot view User & Access Control.",
          );
        }
        return (
          <UserAccessControlPage
            currentUser={user}
            currentRoleName={roleName}
          />
        );
      case "audit-logs":
        return <AuditLogsPage />;
      case "countries":
        return <CountryManagementPage />;
      case "country-create":
        return <CountryCreatePage />;
      case "country-edit":
        return <CountryEditPage />;
      case "cities":
        return <CityManagementPage />;
      case "city-create":
        return <CityCreatePage />;
      case "city-edit":
        return <CityEditPage />;
      case "asset-task-management":
        return <AssetTaskManagementPage />;
      case "asset-task-create":
        return <AssetTaskCreatePage />;
      case "asset-task-edit":
        return <AssetTaskEditPage />;
      case "asset-task-detail":
        return <AssetTaskDetailPage />;
      default:
        return <DashboardPage />;
    }
  };

  return (
    <MainLayout
      user={user}
      onLogout={onLogout}
      activeSection={activeSection}
      setActiveSection={handleSectionChange}
    >
      <AppProvider
        value={{
          activeSection,
          setActiveSection: handleSectionChange,
          layoutCompanyId,
          setLayoutCompanyId,
          buildingId,
          setBuildingId,
          buildingFormMode,
          setBuildingFormMode,
          floorId,
          setFloorId,
          floorFormMode,
          setFloorFormMode,
          functionId,
          setFunctionId,
          functionFormMode,
          setFunctionFormMode,
          jobId,
          setJobId,
          jobFormMode,
          setJobFormMode,
          assetTaskId,
          setAssetTaskId,
          kpiId,
          setKpiId,
        }}
      >
        <div className="page-router">{renderContent()}</div>
      </AppProvider>
    </MainLayout>
  );
};

export default Dashboard;
